import { batch } from 'react-redux'
import { jwtDecode } from 'jwt-decode'
import { formatDNI } from '@chilecompra/react-kit/utils'
import { deleteCookie, getCookie, putCookie } from '@chilecompra/react-kit/storages'
import { makeActionCreator } from '../../config/store/utils'
import { REACT_APP_API_BASE_URL } from '../../config/settings/environment'
import { getUniquePassToken, getUniquePassUserInfo, logoutUniquePass } from '../../services/auth'
import { customizeError } from '@chilecompra/react-kit/errors'
import { DNI_TEST } from '../../config/settings/constants'

export const GET_TOKEN = 'GET_TOKEN'
export const GET_TOKEN_ERROR = 'GET_TOKEN_ERROR'
export const GET_TOKEN_SUCCESS = 'GET_TOKEN_SUCCESS'
export const onGetToken = makeActionCreator(GET_TOKEN)
export const onGetTokenError = makeActionCreator(GET_TOKEN_ERROR, 'payload')
export const onGetTokenThunk = payload => async dispatch => {
  dispatch(onGetToken())
  try {
    const backup = await Promise.resolve()
      .then(() => getCookie('registerWizardBackup', { base64: true, json: true }))
      .catch(() => {
        throw customizeError({ code: 400, reason: 'SOMETHING_WENT_WRONG_ERROR' })
      })

    const { data } = await getUniquePassToken({ code: payload.code, state: payload.csrfToken }).catch(() => {
      throw customizeError({ code: 400, originalError: backup, reason: 'SOMETHING_WENT_WRONG_ERROR' })
    })

    if (backup?.isRecoveryAdmin) {
      dispatch(onGetUniquePassRecoveryInfoThunk({ token: data.payload.access_token, backup }))
    } else {
      dispatch(onGetUserInfoThunk({ token: data.payload.access_token, refreshToken: data.payload.refresh_token }))
    }
  } catch (error) {
    const { originalError: backup, reason } = error.toObject()

    return dispatch(onGetTokenError({ backup, reason }))
  }
}

export const GET_UNIQUE_PASS_RECOVERY_INFO = 'GET_UNIQUE_PASS_RECOVERY_INFO'
export const GET_UNIQUE_PASS_RECOVERY_INFO_ERROR = 'GET_UNIQUE_PASS_RECOVERY_INFO_ERROR'
export const GET_UNIQUE_PASS_RECOVERY_INFO_SUCCESS = 'GET_UNIQUE_PASS_RECOVERY_INFO_SUCCESS'
export const onGetUniquePassRecoveryInfo = makeActionCreator(GET_UNIQUE_PASS_RECOVERY_INFO)
export const onGetUniquePassRecoveryInfoError = makeActionCreator(GET_UNIQUE_PASS_RECOVERY_INFO_ERROR, 'payload')
export const onGetUniquePassRecoveryInfoSuccess = makeActionCreator(GET_UNIQUE_PASS_RECOVERY_INFO_SUCCESS, 'payload')
export const onGetUniquePassRecoveryInfoThunk = payload => async dispatch => {
  dispatch(onGetUniquePassRecoveryInfo())
  deleteCookie('registerWizardBackup')

  try {
    const { data } =
      (await getUniquePassUserInfo({ token: payload.token }).catch(() => {
        throw customizeError({ code: 400, originalError: payload.backup, reason: 'SOMETHING_WENT_WRONG_ERROR' })
      })) || {}

    const decoded = jwtDecode(payload.token)
    const { preferred_username: number, digito_verificador: digit } = data?.payload || decoded || {}
    const dni = formatDNI(number + digit)

    if (dni !== payload.backup?.guest.dni) {
      throw customizeError({ code: 400, originalError: payload.backup, reason: 'DNI_DOES_NOT_MATCH_UNIQUE_PASS_ERROR' })
    }

    return dispatch(
      onGetUniquePassRecoveryInfoSuccess({
        backup: payload.backup
      })
    )
  } catch (error) {
    const { originalError: backup, reason } = error.toObject()

    return dispatch(onGetUniquePassRecoveryInfoError({ backup, reason }))
  }
}

export const GET_USER_INFO = 'GET_USER_INFO'
export const GET_USER_INFO_ERROR = 'GET_USER_INFO_ERROR'
export const GET_USER_INFO_SUCCESS = 'GET_USER_INFO_SUCCESS'
export const onGetUserInfo = makeActionCreator(GET_USER_INFO)
export const onGetUserInfoError = makeActionCreator(GET_USER_INFO_ERROR, 'payload')
export const onGetUserInfoSuccess = makeActionCreator(GET_USER_INFO_SUCCESS, 'payload')
export const onGetUserInfoThunk = payload => async dispatch => {
  dispatch(onGetUserInfo())
  deleteCookie('registerWizardBackup')

  try {
    const responseUniquePass = (await getUniquePassUserInfo({ token: payload.token }).catch(error => error)) || {}

    const decoded = jwtDecode(payload.token)
    const { preferred_username: number, digito_verificador: digit } = responseUniquePass?.data?.payload || decoded || {}
    const dni = formatDNI(number + digit)

    if (DNI_TEST.includes(dni)) {
      return batch(() => {
        dispatch(
          onGetUserInfoSuccess({
            dni,
            firstLastName: 'Iturriaga',
            gender: 2,
            name: 'Marianela Andrea',
            secondLastName: 'Jiménez'
          })
        )
        dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
      })
    }

    const uniqueInfo = responseUniquePass?.data?.payload || {}

    if (uniqueInfo?.nombres) {
      return batch(() => {
        dispatch(
          onGetUserInfoSuccess({
            dni,
            name: uniqueInfo?.nombres || '',
            firstLastName: uniqueInfo?.apellidoPaterno || '',
            secondLastName: uniqueInfo?.apellidoMaterno || ''
          })
        )
        dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
      })
    }
  } catch (error) {
    return batch(() => {
      dispatch(onGetUserInfoError({ error }))
      dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
    })
  }
}

export const LOGOUT_UNIQUE_PASS = 'LOGOUT_UNIQUE_PASS'
export const LOGOUT_UNIQUE_PASS_ERROR = 'LOGOUT_UNIQUE_PASS_ERROR'
export const LOGOUT_UNIQUE_PASS_SUCCESS = 'LOGOUT_UNIQUE_PASS_SUCCESS'
export const onLogoutUniquePass = makeActionCreator(LOGOUT_UNIQUE_PASS)
export const onLogoutUniquePassError = makeActionCreator(LOGOUT_UNIQUE_PASS_ERROR, 'payload')
export const onLogoutUniquePassSuccess = makeActionCreator(LOGOUT_UNIQUE_PASS_SUCCESS, 'payload')
export const onLogoutUniquePassThunk =
  ({ refreshToken }) =>
  async dispatch => {
    dispatch(onLogoutUniquePass())
    try {
      await logoutUniquePass({ refreshToken })

      return dispatch(onLogoutUniquePassSuccess())
    } catch (error) {
      return dispatch(onLogoutUniquePassError({ error }))
    }
  }

export const onSignInUniquePassThunk = payload => (_, getState) => {
  let registerWizardState = getState().registerWizard
  const uniquePassURL = `${REACT_APP_API_BASE_URL}/v1/registro-civil/clave-unica/sso/authorize?csrf=${payload.csrfToken}&aplicativo=inscripcion-proveedor`
  if (payload.isRecovery) {
    registerWizardState = { ...registerWizardState, isRecoveryAdmin: true, isUniquePass: true }
  }

  putCookie(
    'registerWizardBackup',
    { ...registerWizardState, business: { ...registerWizardState.business, dni: payload.dni } },
    { base64: true, json: true }
  )

  window.location.href = uniquePassURL
}
